// noinspection JSUnusedGlobalSymbols

import {CaosCommand, GameVariant, Nullable, ValuesList} from "./caos-def";
import {escapeRegExp, isBlankOrNull} from "./utils.string";
import {hasProperty} from "./utils.objects";
import {getVariantFlag} from "./utils.variants";
import {getValuesLists, isValuesList} from "./utils.valueslists";
import {isCommand} from "./utils.commands";
import {CaosLib} from "./utils.caosdef";

/**
 * Filter for Command and/or values lists
 */
export type ItemFilter = (command: CaosCommand|ValuesList)=> boolean;


/**
 * Object holding the filters and filter creators
 */
export namespace CommandFilters {

    // Cache list of variant filters for faster retrieval
    // prevents have to get all values from associative arrays each time
    const variantFilters: {[id: GameVariant|string]: ItemFilter} = {};

    export const SEARCH_DESCRIPTIONS_DEFAULT = false;

    /**
     * Allow everything through filter
     */
    export const allCommands: ItemFilter = () => true;


    /**
     * Creates a filter function for searching commands and values list
     * @param query
     * @param searchDescription whether to search in descriptions as well as without
     */
    export function createSearchFilter(query: string, searchDescription: boolean): ItemFilter {

        if (query.trim().length == 0) {
            return allCommands;
        }

        if (searchDescription) {
            return getSearchByNameAndDescriptionFilter(query);
        } else {
            return getSearchByNameOrCommandFilter(query);
        }
    }

    /**
     * Get a filter that filters commands and values lists by variant
     * @param caosLib
     * @param variant
     */
    export function getVariantsFilter(caosLib: CaosLib, variant: Nullable<GameVariant|"AL">): ItemFilter {
        if (isBlankOrNull(variant) || variant?.toUpperCase() === "AL") {
            return allCommands;
        }

        // Normalize variant to uppercase
        variant = variant!.toUpperCase() as GameVariant;


        // Return existing variant filter if it exists
        if (hasProperty(variantFilters, variant)) {
            return variantFilters[variant];
        }

        // Get bitmask flag for variant for command variant check
        const flag = getVariantFlag(variant);

        // Get ids for all commands in variant
        const validCommandsForVariants = caosLib.getCommands()
            .filter(c => (c.variants & flag) === flag)
            .map(c => c.id);

        // Get all values lists for variant
        const validValuesListsForVariant = getValuesLists(caosLib, variant)
            .map(list => list.id)


        // Create a variant filter handling both commands and values lists
        const filter = (item: CaosCommand|ValuesList) => {
            if (isCommand(item)) {
                return validCommandsForVariants.indexOf(item.id) >= 0;
            }
            if (isValuesList(item)) {
                return validValuesListsForVariant.indexOf(item.id) >= 0;
            }
            return false;
        }

        // Cache variant filter
        variantFilters[variant] = filter;

        // return filter (of course)
        return filter;
    }
}


const whitespaceRegex = /\s+/g;

function getSearchByNameOrCommandFilter(query: string): ItemFilter {
    const regex = getRegex(query);

    return (item: CaosCommand|ValuesList) => {
        return testItem(item, regex)
    }
}

function getSearchByNameAndDescriptionFilter(query: string): ItemFilter {

    if (query.trim().length == 0) {
        return CommandFilters.allCommands;
    }



    const regex = getRegex(query);

    const searchBody = getMultiWordSearch(query);

    return (item: CaosCommand|ValuesList) => {
        if (testItem(item, regex)) {
            return true;
        }
        if (isBlankOrNull(item.description)) {
            return false;
        }
        if (regex.test(item.description!.replace(whitespaceRegex, ""))) {
            return true
        }
        const searchableText = getItemName(item) + " " + (item.description!)
        return searchBody?.call(null, searchableText) != false;
    }
}


const multiWordStripRegex = /[^a-zA-Z0-9_ ]+/gi;

function getMultiWordSearch(query: string): Nullable<(item: string) => boolean> {

    query = query
        .replace(multiWordStripRegex, "")
        .trim();

    if (query.length == 0) {
        return null;
    }

    if (query.indexOf(" ") < 0) {
        return (item: string): boolean => {
            return item
                .replace(/(\\t|\\n)/gi, " ")
                .replace(multiWordStripRegex, "")
                .indexOf(query) >= 0;
        }
    }


    const words = query
        .toLowerCase()
        .replace(/\s\s+/g, " ")
        .split(" ")
        .filter((item) => item.trim().length > 0)
        .map(word => {
            return new RegExp(`( ${word}|^${word})`, "ig")
        });

    if (words.length === 0) {
        return null;
    }

    return (item: string): boolean => {
        item = item.replace(multiWordStripRegex, "").trim();
        if (item.length === 0) {
            return false;
        }
        for (const word of words) {
            if (!word.test(item)) {
                return false;
            }
        }
        return true;
    }

}


function testItem(item: unknown, test: RegExp): boolean {
    if (isCommand(item)) {
        return test.test(item.command.replace(whitespaceRegex, ""));
    } else if (isValuesList(item)) {
        return test.test(item.name);
    } else if (typeof item === "string") {
        return test.test(item);
    } else {
        return false;
    }
}

function getItemName(item: unknown): Nullable<string> {
    if (isCommand(item)) {
        return item.command;
    } else if (isValuesList(item)) {
        return item.name;
    } else if (typeof item === "string") {
        return item;
    } else {
        return null;
    }
}

function getRegex(query: string): RegExp {
    // Skip spaces
    const queryAsRegexString = escapeRegExp(query.trim())
        .replace(whitespaceRegex, "");
    return new RegExp(queryAsRegexString, "i");
}
